import React, { useEffect, useMemo, useState } from 'react'

import type { TQueryOrderArray, TQueryOrderObject } from '@cubejs-client/core'
import {
  CubeOperator,
  DRIFT_CHAT_OPPORTUNITY_OVERVIEW,
  routingOutcomeOptions,
} from 'cube'

import {
  AccountAvatar,
  Flex,
  Icon,
  IconButton,
  Link,
  Skeleton,
  StatusBadge,
  Table,
  TableColumn,
  TableSortingState,
  TableToolbar,
  Text,
  UserAvatar,
  useTableRef,
} from '@driftt/dds'
import { faArrowUpRightFromSquare } from '@driftt/dds.icons/light/faArrowUpRightFromSquare'
import { faEye } from '@driftt/dds.icons/light/faEye.js'
import {
  CubeQueryBuilder,
  CubeQueryStringAdapter,
  TypeFormats as ReportTypeFormats,
} from '@driftt/dds.reports'

import { CONVO_DETAIL_REPORT_PATH } from 'layouts/ConversationsDetailReport/ConversationsDetailReport.constants'
import { OpportunitiesTableRow } from 'layouts/RevenueReport'
import { OpportunityAttribution } from 'layouts/RevenueReport/components/OpportunityAttribution'
import useOpportunities, {
  useFetchSalesForceDomainAPI,
} from 'layouts/RevenueReport/Opportunities/Opportunities.hook'
import { TypeFormats } from 'utils/format'
import { isNilOrEmpty } from 'utils/ramda'

import { useOpportunitiesStore } from '../Opportunities.context'
import { getOpportunityStageVariant } from '../Opportunities.utils'
import { OpportunityTimeline } from '../OpportunityTimeline'

const mapOrderToTableSorting = (
  order: TQueryOrderArray | TQueryOrderObject
) => {
  const sortingList: TQueryOrderArray = order as TQueryOrderArray

  return (
    sortingList?.map(([key, value]) => {
      return {
        id: `${key.split('.').pop()}` as string,
        desc: value === 'desc',
      }
    }) || []
  )
}

const getOpportunityNameFilterDrillDownQueryString = (
  opportunityName: string,
  startDate: Date,
  endDate: Date
) => {
  const conversationCountQuery = new CubeQueryBuilder({})
    .withFilters([
      {
        member: DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_NAME,
        operator: CubeOperator.EQUALS,
        values: [opportunityName],
      },
    ])
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.FIRST_CONVERSATION_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  return CubeQueryStringAdapter.toQueryString(conversationCountQuery)
}

export const OpportunitiesTable = () => {
  const tableRef = useTableRef()

  const [currentOpportunityIndex, setCurrentOpportunityIndex] = useState<
    number | null
  >(null)

  const [selectedOpportunity, setSelectedOpportunity] =
    useState<OpportunitiesTableRow | null>(null)

  const { domain } = useFetchSalesForceDomainAPI()

  const {
    store: { order, endDate },
  } = useOpportunitiesStore()

  const tableSortingState: TableSortingState<OpportunitiesTableRow>[] =
    mapOrderToTableSorting(order)

  const { data, isLoading, totalRowCount, pagination, handleTableStateChange } =
    useOpportunities({ orderBy: order })

  const defaultColumns: TableColumn<OpportunitiesTableRow>[] = useMemo(
    () => [
      {
        id: 'opportunityId',
        header: '',
        value: 'opportunityId',
        cell: (opportunityRow) => (
          <IconButton
            title={'Open opportunity timeline'}
            icon={faEye}
            onClick={() =>
              setCurrentOpportunityIndex(
                data.findIndex(
                  ({ opportunityId }) =>
                    opportunityId === opportunityRow.opportunityId
                )
              )
            }
            size="default"
          />
        ),
        size: 56,
      },
      {
        id: 'opportunityName',
        header: 'Opportunity',
        value: 'opportunityName',
        cell: ({ opportunityName }) => (
          <Text variant="bodyBold">{opportunityName}</Text>
        ),
        size: 220,
      },
      {
        id: 'opportunityType',
        header: 'Type',
        value: 'opportunityType',
        cell: ({ opportunityType }) => (
          <Text variant="body">{opportunityType}</Text>
        ),
        size: 120,
      },
      {
        id: 'opportunityAmount',
        header: 'Amount',
        value: 'opportunityAmount',
        cell: ({ opportunityAmount }) => (
          <Text variant="body">
            {TypeFormats.CURRENCY_UNROUNDED(opportunityAmount)}
          </Text>
        ),
        size: 120,
      },
      {
        id: 'attribution',
        header: 'Attribution',
        value: 'isInfluenced',
        cell: ({ isInfluenced, isSourced }) => (
          <OpportunityAttribution
            isInfluenced={isInfluenced}
            isSourced={isSourced}
          />
        ),
        size: 120,
      },
      {
        id: 'firstContactDisplayName',
        header: 'Contact',
        value: 'firstContactDisplayName',
        cell: ({ firstContactDisplayName }) => (
          <Flex
            fullWidth
            fullHeight
            justifyContent="flex-start"
            alignItems="center"
          >
            <UserAvatar name={firstContactDisplayName} size={'small'} />
            <Text>{firstContactDisplayName}</Text>
          </Flex>
        ),
        size: 120,
      },
      {
        id: 'companyName',
        header: 'Company',
        value: 'companyName',
        cell: ({ companyName }) => (
          <Flex
            fullWidth
            fullHeight
            justifyContent="flex-start"
            alignItems="center"
          >
            <AccountAvatar size="x-small" />
            <Text variant="body">{companyName}</Text>
          </Flex>
        ),
        size: 200,
      },
      {
        id: 'opportunityCreatedAtWithZone',
        header: 'Created',
        value: 'opportunityCreatedAtWithZone',
        cell: ({ opportunityCreatedAtWithZone }) => (
          <Text variant="body">
            {TypeFormats.DATE_SLASH(opportunityCreatedAtWithZone)}
          </Text>
        ),
        size: 100,
      },
      {
        id: 'opportunityClosedAtWithZone',
        header: 'Closed',
        value: 'opportunityClosedAtWithZone',
        cell: ({ opportunityClosedAtWithZone }) => (
          <Text variant="body">
            {TypeFormats.DATE_SLASH(opportunityClosedAtWithZone)}
          </Text>
        ),
        size: 100,
      },
      {
        id: 'opportunityStage',
        header: 'Stage',
        value: 'opportunityStage',
        cell: ({ opportunityStage = '' }) => (
          <Text variant="body">
            <StatusBadge variant={getOpportunityStageVariant(opportunityStage)}>
              {opportunityStage}
            </StatusBadge>
          </Text>
        ),
        size: 100,
      },
      {
        id: 'opportunityId',
        header: 'Salesforce',
        value: 'opportunityId',
        cell: ({ opportunityId }) => (
          <Flex
            fullWidth
            fullHeight
            alignItems="center"
            justifyContent="center"
          >
            {domain ? (
              <Link
                target="_blank"
                variant="inline"
                external={true}
                href={`${domain}/${opportunityId}`}
              >
                <Icon icon={faArrowUpRightFromSquare} />
              </Link>
            ) : (
              '—'
            )}
          </Flex>
        ),
        size: 100,
      },
      {
        id: 'opportunityDaysInCycle',
        header: 'Days in cycle',
        value: 'opportunityDaysInCycle',
        cell: ({ opportunityDaysInCycle = '' }) => {
          return <Text variant="body">{opportunityDaysInCycle}</Text>
        },
        align: 'right',
        size: 125,
      },
      {
        id: 'conversationCount',
        header: 'Conversation(s)',
        value: 'conversationCount',
        cell: ({
          conversationCount,
          opportunityName = '',
          firstConversationCreatedAt = '',
        }) => {
          const drillDownQueryString =
            getOpportunityNameFilterDrillDownQueryString(
              opportunityName,
              new Date(firstConversationCreatedAt),
              endDate
            )
          return (
            <Link
              target="_blank"
              variant="inline"
              external={false}
              href={`${CONVO_DETAIL_REPORT_PATH}?${drillDownQueryString}`}
            >
              {conversationCount}
            </Link>
          )
        },
        align: 'right',
        size: 110,
      },
      {
        id: 'lastMeetingBookedByAgentDisplayName',
        header: 'Meeting booked by',
        value: 'lastMeetingBookedByAgentDisplayName',
        cell: ({ lastMeetingBookedByAgentDisplayName }) => (
          <Flex
            fullWidth
            fullHeight
            justifyContent="flex-start"
            alignItems="center"
          >
            {lastMeetingBookedByAgentDisplayName && (
              <UserAvatar
                name={lastMeetingBookedByAgentDisplayName}
                size={'small'}
              />
            )}
            <Text>{lastMeetingBookedByAgentDisplayName}</Text>
          </Flex>
        ),
        size: 150,
      },
      {
        id: 'lastConversationRoutingOutcome',
        value: 'lastConversationRoutingOutcome',
        header: 'Outcome',
        size: 125,
        cell: ({ lastConversationRoutingOutcome }) => {
          return lastConversationRoutingOutcome
            ? routingOutcomeOptions.find(
                (option) => option.value === lastConversationRoutingOutcome
              )?.label ?? lastConversationRoutingOutcome
            : '—'
        },
      },
    ],

    [data, domain, endDate]
  )

  useEffect(() => {
    if (isNilOrEmpty(currentOpportunityIndex)) {
      setSelectedOpportunity(null)
    } else if (!isNilOrEmpty(data?.[currentOpportunityIndex])) {
      setSelectedOpportunity(data[currentOpportunityIndex])
    }
  }, [data, currentOpportunityIndex])

  const handleNavigatePrev = () => {
    const newConvoIndex = isNilOrEmpty(currentOpportunityIndex)
      ? 0
      : currentOpportunityIndex - 1

    if (newConvoIndex > -1) {
      setCurrentOpportunityIndex(newConvoIndex)
      return
    }

    if (newConvoIndex === -1 && pagination.pageIndex > 0) {
      tableRef.current?.reset({
        pagination: {
          pageIndex: pagination.pageIndex - 1,
          pageSize: pagination.pageSize,
        },
      })
      setCurrentOpportunityIndex(pagination.pageSize - 1)
    }
  }

  const handleNavigateNext = () => {
    const newConvoIndex = isNilOrEmpty(currentOpportunityIndex)
      ? 0
      : currentOpportunityIndex + 1

    if (newConvoIndex <= data.length - 1) {
      setCurrentOpportunityIndex(newConvoIndex)
      return
    }

    const totalPages = totalRowCount
      ? Math.ceil(totalRowCount / pagination.pageSize)
      : 0

    if (pagination.pageIndex < totalPages - 1) {
      tableRef.current?.reset({
        pagination: {
          pageIndex: pagination.pageIndex + 1,
          pageSize: pagination.pageSize,
        },
      })
      setCurrentOpportunityIndex(0)
    }
  }

  return (
    <>
      <Table<OpportunitiesTableRow>
        tableRef={tableRef}
        rows={data}
        columns={defaultColumns}
        isLoading={isLoading}
        disableBoxShadow={true}
        loadingRows={10}
        pagination={{
          rowTotal: totalRowCount ?? 0,
          defaultPagination: pagination,
        }}
        onStateChange={handleTableStateChange}
        renderToolbar={({ table }) => {
          return (
            <Flex
              justifyContent="space-between"
              css={{
                padding: '$0 $2 $1 $2',
                alignItems: 'baseline',
              }}
              fullWidth
            >
              <Skeleton isLoading={isLoading} height={32} width={80}>
                <Flex alignItems="center">
                  <Text variant="bodyBold">
                    {ReportTypeFormats.RAW_HUMANIZED(totalRowCount ?? 0)}{' '}
                    {ReportTypeFormats.PLURALIZE(
                      totalRowCount ?? 0,
                      'opportunity',
                      'opportunities'
                    )}
                  </Text>
                </Flex>
              </Skeleton>
              <TableToolbar.VisibilityMenu table={table} />
            </Flex>
          )
        }}
        sorting={{
          disableSort: true, // NOTE: until this feature is requested this should be disabled
          value: tableSortingState,
          // NOTE: onChange should be implemented when enabling sorting through UI
        }}
        highlightedRow={(data) =>
          !isNilOrEmpty(selectedOpportunity) &&
          data?.opportunityId === selectedOpportunity?.opportunityId
        }
      />
      <OpportunityTimeline
        opportunity={selectedOpportunity}
        onClose={() => setCurrentOpportunityIndex(null)}
        isLoadingTableData={isLoading}
        onPrevItem={handleNavigatePrev}
        onNextItem={handleNavigateNext}
      />
    </>
  )
}
